// Widget installation adapter api module — the generator-facing surface for the
// dashboard-v2 Widget Studio installation screens.
//
// CHG-2463 / ITER-905205 — Widget Studio S4 (installations CRUD).
//
// Maps the ListPage/DetailPage queryFn contract onto the landed widgets-admin
// client (widgets-admin.ts) the same way api/widget-instances.ts does for S3.
// The only admin write is the inline status override (PATCH
// /admin/widgets/installations/{id}); there is no GET-by-id route, so the
// detail resolves the row by scanning the org list.

import { widgetsAdminApi } from "./widgets-admin";
import type { WidgetInstallationStatus } from "./widgets-admin";
import type { WidgetInstallation } from "./types";

export interface ListWidgetInstallationsParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
  // Opaque filter dict from the v2 ListPage filterChips/filters slots. Keys
  // match the entity-hint (status, widget_instance_id).
  filters?: Record<string, string | number | boolean | undefined>;
}

// GET /admin/widgets/installations mapped to the ListPage contract. `search`
// (free-text domain) and sort are accepted to satisfy the generator queryFn
// shape but are forward-promised — the admin list endpoint exposes no such
// param yet, so they are not forwarded today (FastAPI tolerates the absence;
// chips render and go live when the BE adds them). status + widget_instance_id
// filters ARE wired through.
export async function listWidgetInstallations(
  params: ListWidgetInstallationsParams = {},
): Promise<{ items: WidgetInstallation[]; total: number }> {
  const pageSize = params.pageSize ?? 50;
  const page = params.page ?? 0;
  const f = params.filters ?? {};
  const env = await widgetsAdminApi.listInstallations({
    status: f.status as WidgetInstallationStatus | undefined,
    widget_instance_id: f.widget_instance_id as string | undefined,
    domain: f.domain as string | undefined,
    limit: pageSize,
    offset: page * pageSize,
  });
  return { items: env.items, total: env.total };
}

// No GET-by-id route on the admin router — resolve via the org list. The
// per-installation counters (impression/viewable/click/render) are
// forward-promised aggregates the endpoint will sum; default them to 0 so the
// detail status cards render a number rather than NaN until the BE projection
// lands. A real value (once the API returns one) wins over the default.
export async function getWidgetInstallation(id: string): Promise<WidgetInstallation> {
  const env = await widgetsAdminApi.listInstallations({ limit: 200, offset: 0 });
  const found = env.items.find((i) => i.id === id);
  if (!found) {
    throw new Error(`Widget installation ${id} not found`);
  }
  const agg = found as Partial<WidgetInstallation>;
  return {
    ...found,
    impression_count: agg.impression_count ?? 0,
    viewable_count: agg.viewable_count ?? 0,
    click_count: agg.click_count ?? 0,
    render_count: agg.render_count ?? 0,
  };
}

export const widgetInstallationsApi = {
  listWidgetInstallations,
  getWidgetInstallation,
};
